import { useState, useEffect, type ComponentProps } from 'react'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { toast } from 'sonner'
import { Skeleton } from 'skullet/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Eye, FileText, Link2, Mail, MessagesSquare, Plus, Send, StickyNote, CheckSquare, Paperclip, Upload, Workflow } from 'lucide-react'
import type { Note, TaskLite } from '@/gen/cortado/v1/actions_pb'
import type { AttachmentLite } from '@/gen/cortado/v1/attachments_pb'
import { ContactQuoteInteractionSchema, type ContactQuoteInteraction } from '@/gen/cortado/v1/invoices_pb'
import type { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { LeadTasksTable } from '@/components/leads/id/lead-tasks-table'
import { TaskSheet } from '@/components/tasks'
import { NoteSheet, NoteList } from '@/components/notes'
import { AttachmentSheet, AttachmentList } from '@/components/attachments'
import { useDeleteAttachment } from '@/stores/attachments.store'
import { getErrorDescription } from '@/lib/error-utils'
import { EmailHistoryList } from '@/components/emails/email-history-list'
import {
  CONVERSATIONS_PAGE_LIMIT,
  EntityConversationsPanel,
} from '@/components/emails/entity-conversations-panel'
import { BetaBadge } from '@/components/shared/beta-badge'
import {
  EmailComposeSheet,
  type EmailComposeTemplateContext,
} from '@/components/emails/email-compose-sheet'
import { listEmails, listPortalVisitorActivity } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListEmailsRequestSchema } from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum as ParentType, QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { QuoteStatusBadge } from '@/components/shared/enum-badge'
import { formatTimestamp } from '@/lib/date-utils'
import { EntityWorkflowRuns } from '@/components/shared/entity-workflow-runs'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { useWorkflowExecutions } from '@/stores/workflows.store'
import {
  ListPortalVisitorActivityRequestSchema,
  ListPortalVisitorActivityResponseSchema,
  PortalVisitorActivitySchema,
  WorkflowExecutionStatus,
} from '@/gen/cortado/v1/cortado_pb'
import { portalActivityHeadline } from '@/lib/portal-activity-copy'
import { EMAIL_COMPOSE_NEEDS_INBOX_MESSAGE } from '@/lib/email-compose'
import { useEmailThreadsByEntity } from '@/stores/emails.store'

const SAMPLE_QUOTE_INTERACTIONS: ContactQuoteInteraction[] = [1042, 1043].map((quoteNumber, i) =>
  create(ContactQuoteInteractionSchema, {
    id: `sample-${i}`,
    quoteNumber,
    quoteTitle: 'Spring garden reception',
    quoteStatus: QuoteStatusEnum.QUOTE_STATUS_SENT,
    recipientEmail: 'client@example.com',
    viewCount: 3,
    firstViewedAt: timestampFromDate(new Date(2026, 0, 5, 10, 15)),
    lastViewedAt: timestampFromDate(new Date(2026, 0, 6, 16, 40)),
  }),
)

const SAMPLE_PORTAL_ACTIVITY = create(ListPortalVisitorActivityResponseSchema, {
  totalOpens: 6n,
  uniqueBrowsers: 2n,
  intendedRecipients: 1n,
  unidentifiedOpenCount: 1n,
  activities: [1, 2].map((n) =>
    create(PortalVisitorActivitySchema, {
      id: `sample-${n}`,
      intendedContactName: 'Jordan Ellis',
      openCount: 3,
      firstOpenedAt: timestampFromDate(new Date(2026, 0, n, 9, 0)),
      lastOpenedAt: timestampFromDate(new Date(2026, 0, n + 1, 18, 30)),
      deviceLabel: 'Chrome on macOS',
      locationLabel: 'Portland, OR',
    }),
  ),
})

interface ActivityTabsProps {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  notes?: Note[]
  tasks?: TaskLite[]
  attachments?: AttachmentLite[]
  quoteInteractions?: ContactQuoteInteraction[]
  quoteInteractionCount?: number
  quoteInteractionsLoading?: boolean
  showQuoteInteractions?: boolean
  /** Customer email address for email compose */
  customerEmail?: string
  /** Inquiry context used for richer template rendering when available */
  emailInquiryId?: string
  /** Contact context used for richer template rendering when available */
  emailContactId?: string
  /** When true (and the org has the workflows feature), shows a Workflows tab
   *  listing the entity's workflow runs. Requires the two workflow* props. */
  showWorkflowRuns?: boolean
  /** Entity id that scopes the workflow runs (inquiry id for leads, booking id
   *  for events) — the execution's source_resource_id. */
  workflowSourceResourceId?: string
  /** UPPERCASE trigger entity type for the workflow runs, e.g. "INQUIRY". */
  workflowTriggerEntityType?: string
  /** Event Staff hold no notes/tasks/attachments access in v1 - hides those
   *  tabs, their panels, and their add affordances entirely. */
  hideNotesTasksAttachments?: boolean
  /** Hides only the Notes and Tasks tabs (Attachments stays), for a page that
   *  shows notes and tasks in its own cards. */
  hideNotesAndTasks?: boolean
  /** Card heading; defaults to "Activity". */
  title?: string
  /** Card subheading; defaults to "Notes, tasks, and communication". */
  description?: string
  /** Event Staff have a fixed read-only scope with no Leads, Invoices,
   *  Quotes, Comms, or Payments access - hides the emails, conversations,
   *  quotes, portal, and workflow-runs tabs, their panels, and Compose/Add. */
  isEventStaff?: boolean
  /** Tab to open first, e.g. from a `?tab=conversations` link. Ignored when
   *  that tab isn't shown to this user. */
  initialTab?: 'conversations'
  /** Conversation thread to open on the Conversations tab, e.g. from a
   *  `?thread=` link in a reply notification. */
  initialThreadId?: string
}
type BadgeVariant = ComponentProps<typeof Badge>['variant']

function formatInteractionDate(timestamp: ContactQuoteInteraction['firstViewedAt']) {
  return timestamp ? formatTimestamp(timestamp, 'MMM d, yyyy h:mm a') : '-'
}

function formatViewCount(viewCount: number) {
  return `${viewCount} ${viewCount === 1 ? 'view' : 'views'}`
}

function getRecipientStatus(interaction: ContactQuoteInteraction): {
  label: string
  variant: BadgeVariant
  timestamp?: ContactQuoteInteraction['acceptedAt']
} {
  if (interaction.acceptedAt) {
    return { label: 'Accepted', variant: 'success', timestamp: interaction.acceptedAt }
  }

  if (interaction.lastViewedAt) {
    return { label: 'Viewed', variant: 'info' }
  }

  return { label: 'Pending', variant: 'secondary' }
}

function getTemplateContext(parentType: ParentTypeEnum): EmailComposeTemplateContext {
  switch (parentType) {
    case ParentType.PARENT_TYPE_INQUIRY:
      return 'inquiry'
    case ParentType.PARENT_TYPE_BOOKING:
      return 'booking'
    case ParentType.PARENT_TYPE_QUOTE:
      return 'quote'
    case ParentType.PARENT_TYPE_INVOICE:
      return 'invoice'
    default:
      return 'general'
  }
}

export function ActivityTabs({
  organizationId,
  parentType,
  parentId,
  notes = [],
  tasks = [],
  attachments = [],
  quoteInteractions = [],
  quoteInteractionCount,
  quoteInteractionsLoading = false,
  showQuoteInteractions: showQuoteInteractionsProp,
  customerEmail,
  emailInquiryId,
  emailContactId,
  showWorkflowRuns = false,
  workflowSourceResourceId,
  workflowTriggerEntityType,
  hideNotesTasksAttachments = false,
  hideNotesAndTasks = false,
  title = 'Activity',
  description = 'Notes, tasks, and communication',
  isEventStaff = false,
  initialTab,
  initialThreadId,
}: ActivityTabsProps) {
  const showNotesAndTasks = !hideNotesTasksAttachments && !hideNotesAndTasks
  const showAttachments = !hideNotesTasksAttachments
  const isEmailParent =
    parentType === ParentType.PARENT_TYPE_INQUIRY ||
    parentType === ParentType.PARENT_TYPE_CONTACT ||
    parentType === ParentType.PARENT_TYPE_BOOKING
  const isContactParent = parentType === ParentType.PARENT_TYPE_CONTACT
  // Conversations: driven by ListEmailThreadsByEntity, which works for any
  // record that carries email_thread_links and rolls up related records (a
  // lead's quotes and invoices, a contact's leads). Every tenant send is filed
  // under a linked thread, so for Connected inbox orgs it replaces the legacy
  // Emails tab. ListEmailThreadsByEntity needs that add-on, so orgs without it
  // keep the Emails tab (deprecated ListEmails) on leads, contacts and bookings.
  const isConversationsParent =
    isEmailParent ||
    parentType === ParentType.PARENT_TYPE_QUOTE ||
    parentType === ParentType.PARENT_TYPE_INVOICE ||
    parentType === ParentType.PARENT_TYPE_COMPANY
  // Compose sends from a connected inbox, which needs the EmailInbox add-on.
  const { hasFeature: hasEmailInbox, hasLoaded: emailInboxFeatureLoaded } = useHasEmailInboxFeature()
  const showEmailsTab = isEmailParent && !isEventStaff && emailInboxFeatureLoaded && !hasEmailInbox
  const showConversationsTab = isConversationsParent && !isEventStaff && hasEmailInbox

  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab === 'conversations' && showConversationsTab) return 'conversations'
    if (showNotesAndTasks) return 'notes'
    if (showConversationsTab) return 'conversations'
    if (showEmailsTab) return 'emails'
    if (showAttachments) return 'attachments'
    return 'notes'
  })
  // A link followed while this page is already open (a notification) changes
  // initialTab without remounting, so honor it here too.
  useEffect(() => {
    if (initialTab === 'conversations' && showConversationsTab) setActiveTab('conversations')
  }, [initialTab, showConversationsTab])
  const [isTaskSheetOpen, setIsTaskSheetOpen] = useState(false)
  const [isNoteSheetOpen, setIsNoteSheetOpen] = useState(false)
  const [isAttachmentSheetOpen, setIsAttachmentSheetOpen] = useState(false)
  const [isEmailComposeOpen, setIsEmailComposeOpen] = useState(false)
  const [editingNote, setEditingNote] = useState<Note | undefined>(undefined)
  const { mutation: deleteAttachmentMutation } = useDeleteAttachment()
  const showQuoteInteractions = (showQuoteInteractionsProp ?? isContactParent) && !isEventStaff
  const showPortalActivity =
    (parentType === ParentType.PARENT_TYPE_INQUIRY ||
      parentType === ParentType.PARENT_TYPE_BOOKING ||
      parentType === ParentType.PARENT_TYPE_CONTACT ||
      parentType === ParentType.PARENT_TYPE_QUOTE ||
      parentType === ParentType.PARENT_TYPE_INVOICE) &&
    !isEventStaff

  const { data: portalActivityData, isLoading: portalActivityLoading } = useQuery(
    listPortalVisitorActivity,
    create(ListPortalVisitorActivityRequestSchema, {
      organizationId,
      parentType,
      parentId,
      limit: 100,
      offset: 0,
    }),
    { enabled: showPortalActivity }
  )

  const { data: emailHistoryData } = useQuery(
    listEmails,
    create(ListEmailsRequestSchema, {
      organizationId,
      parentType,
      parentId,
      limit: 1,
      offset: 0,
    }),
    { enabled: showEmailsTab }
  )
  const emailCount = emailHistoryData?.threadTotalCount ?? emailHistoryData?.totalCount ?? 0
  // Same request as the panel's first page, so the badge and the list share
  // one cached query.
  const { totalCount: conversationCount } = useEmailThreadsByEntity(parentType, parentId, {
    limit: CONVERSATIONS_PAGE_LIMIT,
    offset: 0,
    enabled: showConversationsTab && !!parentId,
  })
  const quoteCount = quoteInteractionCount ?? quoteInteractions.length

  const canCompose = hasEmailInbox
  const showComposeNeedsInbox = emailInboxFeatureLoaded && !hasEmailInbox
  const showWorkflows = showWorkflowRuns && !!workflowSourceResourceId && !isEventStaff
  // Count badge: active (in-flight) runs for this entity. Only queried when the
  // tab is shown.
  const { executions: activeWorkflowRuns } = useWorkflowExecutions({
    sourceResourceId: workflowSourceResourceId,
    triggerEntityType: workflowTriggerEntityType,
    statuses: [WorkflowExecutionStatus.RUNNING, WorkflowExecutionStatus.PENDING],
    pageSize: 25,
    enabled: showWorkflows,
  })
  const activeWorkflowRunCount = activeWorkflowRuns.length

  const visibleTabs = [
    ...(showNotesAndTasks ? ['notes', 'tasks'] : []),
    ...(showAttachments ? ['attachments'] : []),
    ...(showEmailsTab ? ['emails'] : []),
    ...(showConversationsTab ? ['conversations'] : []),
    ...(showQuoteInteractions ? ['quotes'] : []),
    ...(showPortalActivity ? ['portal'] : []),
    ...(showWorkflows ? ['workflows'] : []),
  ]
  const visibleTabsKey = visibleTabs.join('|')

  // The visible-tab set can change after mount (e.g. isEventStaff resolves
  // asynchronously from usePermissions), so re-derive the active tab whenever
  // it stops being one of the rendered tabs rather than leaving it stuck on a
  // now-hidden tab with a blank panel.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on the content signature, not the freshly-allocated array identity
  }, [visibleTabsKey, activeTab])

  const composeInquiryId =
    emailInquiryId || (parentType === ParentType.PARENT_TYPE_INQUIRY ? parentId : undefined)
  const composeContactId =
    emailContactId || (parentType === ParentType.PARENT_TYPE_CONTACT ? parentId : undefined)
  const canComposeEmail = (isEmailParent || isConversationsParent) && !!customerEmail
  const isComposeButtonDisabled =
    (activeTab === 'emails' || activeTab === 'conversations') && !canComposeEmail
  let composeDisabledReason: string | undefined
  if (isComposeButtonDisabled) {
    if (!customerEmail && isContactParent) {
      composeDisabledReason = 'Add an email address to this contact before composing.'
    } else if (!customerEmail) {
      composeDisabledReason = 'Add a customer email before composing.'
    }
  }
  const showAddButton =
    (showNotesAndTasks && (activeTab === 'notes' || activeTab === 'tasks')) ||
    (showAttachments && activeTab === 'attachments') ||
    // Without a connected inbox add-on the email tabs show a note instead of
    // the Compose button.
    (canCompose &&
      ((activeTab === 'emails' && showEmailsTab) ||
        (activeTab === 'conversations' && showConversationsTab)))

  const getButtonText = () => {
    switch (activeTab) {
      case 'notes':
        return 'Add Note'
      case 'tasks':
        return 'Add Task'
      case 'attachments':
        return 'Upload'
      case 'emails':
      case 'conversations':
        return 'Compose'
      default:
        return 'Add'
    }
  }

  const getButtonIcon = () => {
    switch (activeTab) {
      case 'emails':
      case 'conversations':
        return <Send className="h-4 w-4" />
      case 'attachments':
        return <Upload className="h-4 w-4" />
      default:
        return <Plus className="h-4 w-4" />
    }
  }

  const handleAddClick = () => {
    switch (activeTab) {
      case 'notes':
        setEditingNote(undefined)
        setIsNoteSheetOpen(true)
        break
      case 'tasks':
        setIsTaskSheetOpen(true)
        break
      case 'attachments':
        setIsAttachmentSheetOpen(true)
        break
      case 'emails':
      case 'conversations':
        setIsEmailComposeOpen(true)
        break
      default:
        break
    }
  }

  const handleDeleteAttachment = async (id: string) => {
    try {
      await deleteAttachmentMutation.mutateAsync({ id })
      toast.success('Attachment deleted')
    } catch (error) {
      toast.error('Failed to delete attachment', {
        description: getErrorDescription(error),
      })
    }
  }

  const handleEditNote = (note: Note) => {
    setEditingNote(note)
    setIsNoteSheetOpen(true)
  }

  const handleNoteSheetClose = (open: boolean) => {
    setIsNoteSheetOpen(open)
    if (!open) {
      setEditingNote(undefined)
    }
  }

  if (visibleTabs.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="notes" value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
          <div className="flex items-center justify-between gap-4 pb-4 border-b">
            <div className="min-w-0 overflow-x-auto">
              <TabsList className="h-11 p-1 bg-muted/50 rounded-lg gap-0.5 w-fit">
                {showNotesAndTasks && (
                  <>
                    <TabsTrigger
                      value="notes"
                      className="gap-2 h-9 px-3 data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-background/60"
                    >
                      <StickyNote className="h-4 w-4" />
                      Notes
                      {notes.length > 0 && (
                        <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-muted-foreground/20 text-xs font-medium inline-flex items-center justify-center">
                          {notes.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="tasks"
                      className="gap-2 h-9 px-3 data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-background/60"
                    >
                      <CheckSquare className="h-4 w-4" />
                      Tasks
                      {tasks.length > 0 && (
                        <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-muted-foreground/20 text-xs font-medium inline-flex items-center justify-center">
                          {tasks.length}
                        </span>
                      )}
                    </TabsTrigger>
                  </>
                )}
                {showAttachments && (
                  <TabsTrigger
                    value="attachments"
                    className="gap-2 h-9 px-3 data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-background/60"
                  >
                    <Paperclip className="h-4 w-4" />
                    Attachments
                    {attachments.length > 0 && (
                      <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-muted-foreground/20 text-xs font-medium inline-flex items-center justify-center">
                        {attachments.length}
                      </span>
                    )}
                  </TabsTrigger>
                )}
                {showEmailsTab && (
                  <TabsTrigger
                    value="emails"
                    className="gap-2 h-9 px-3 data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-background/60"
                  >
                    <Mail className="h-4 w-4" />
                    Emails
                    <BetaBadge className="px-1.5 py-0" />
                    {emailCount > 0 && (
                      <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-muted-foreground/20 text-xs font-medium inline-flex items-center justify-center">
                        {emailCount}
                      </span>
                    )}
                  </TabsTrigger>
                )}
                {showConversationsTab && (
                  <TabsTrigger
                    value="conversations"
                    className="gap-2 h-9 px-3 data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-background/60"
                  >
                    <MessagesSquare className="h-4 w-4" />
                    Conversations
                    <BetaBadge className="px-1.5 py-0" />
                    {conversationCount > 0 && (
                      <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-muted-foreground/20 text-xs font-medium inline-flex items-center justify-center">
                        {conversationCount}
                      </span>
                    )}
                  </TabsTrigger>
                )}
                {showQuoteInteractions && (
                  <TabsTrigger
                    value="quotes"
                    className="gap-2 h-9 px-3 data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-background/60"
                  >
                    <FileText className="h-4 w-4" />
                    Quotes
                    {quoteCount > 0 && (
                      <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-muted-foreground/20 text-xs font-medium inline-flex items-center justify-center">
                        {quoteCount}
                      </span>
                    )}
                  </TabsTrigger>
                )}
                {showPortalActivity && (
                  <TabsTrigger
                    value="portal"
                    className="gap-2 h-9 px-3 data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-background/60"
                  >
                    <Link2 className="h-4 w-4" />
                    Portal
                    {(portalActivityData?.totalOpens ?? 0n) > 0n && (
                      <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-muted-foreground/20 text-xs font-medium inline-flex items-center justify-center">
                        {portalActivityData?.totalOpens.toString()}
                      </span>
                    )}
                  </TabsTrigger>
                )}
                {showWorkflows && (
                  <TabsTrigger
                    value="workflows"
                    className="gap-2 h-9 px-3 data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-background/60"
                  >
                    <Workflow className="h-4 w-4" />
                    Workflows
                    {activeWorkflowRunCount > 0 && (
                      <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-muted-foreground/20 text-xs font-medium inline-flex items-center justify-center">
                        {activeWorkflowRunCount}
                      </span>
                    )}
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
            {showAddButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={isComposeButtonDisabled ? 0 : undefined}
                    className="inline-flex shrink-0"
                  >
                    <Button
                      size="sm"
                      onClick={handleAddClick}
                      disabled={isComposeButtonDisabled}
                    >
                      {getButtonIcon()}
                      {getButtonText()}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isComposeButtonDisabled && composeDisabledReason && (
                  <TooltipContent side="left" className="max-w-64">
                    {composeDisabledReason}
                  </TooltipContent>
                )}
              </Tooltip>
            )}
          </div>
          <div className="pt-4">
            {showNotesAndTasks && (
              <>
                <TabsContent value="notes" className="mt-0 animate-in fade-in-0 duration-200">
                  <NoteList notes={notes} onEditNote={handleEditNote} />
                </TabsContent>
                <TabsContent value="tasks" className="mt-0 animate-in fade-in-0 duration-200">
                  {tasks.length === 0 ? (
                    <Empty className="border border-dashed py-12">
                      <EmptyMedia variant="icon">
                        <CheckSquare className="h-5 w-5" />
                      </EmptyMedia>
                      <EmptyDescription>No tasks yet</EmptyDescription>
                    </Empty>
                  ) : (
                    <LeadTasksTable data={tasks} />
                  )}
                </TabsContent>
              </>
            )}
            {showAttachments && (
              <TabsContent value="attachments" className="mt-0 animate-in fade-in-0 duration-200">
                <AttachmentList attachments={attachments} onDelete={handleDeleteAttachment} />
              </TabsContent>
            )}
            {showEmailsTab && (
              <TabsContent value="emails" className="mt-0 animate-in fade-in-0 duration-200">
                {showComposeNeedsInbox && <ComposeNeedsInboxNote />}
                <EmailHistoryList
                  organizationId={organizationId}
                  parentType={parentType}
                  parentId={parentId}
                />
              </TabsContent>
            )}
            {showConversationsTab && (
              <TabsContent value="conversations" className="mt-0 animate-in fade-in-0 duration-200">
                <EntityConversationsPanel
                  organizationId={organizationId}
                  parentType={parentType}
                  parentId={parentId}
                  initialThreadId={initialThreadId}
                />
              </TabsContent>
            )}
            {showQuoteInteractions && (
              <TabsContent value="quotes" className="mt-0 animate-in fade-in-0 duration-200">
                <Skeleton
                  data={quoteInteractionsLoading ? null : quoteInteractions}
                  sample={SAMPLE_QUOTE_INTERACTIONS}
                  label="Loading quote activity"
                  empty={!quoteInteractionsLoading && quoteInteractions.length === 0}
                  emptyMessage={
                    <Empty className="border border-dashed py-12">
                      <EmptyMedia variant="icon">
                        <FileText className="h-5 w-5" />
                      </EmptyMedia>
                      <EmptyDescription>No quote activity yet</EmptyDescription>
                    </Empty>
                  }
                >
                  {(quoteInteractions) => (
                    <div className="space-y-2">
                      {quoteInteractions.map((interaction) => {
                        const recipientStatus = getRecipientStatus(interaction)

                        return (
                          <div key={interaction.id} className="rounded-md border p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium">
                                    Quote #{interaction.quoteNumber}
                                  </span>
                                  <QuoteStatusBadge status={interaction.quoteStatus} />
                                  <Badge variant={recipientStatus.variant}>
                                    {recipientStatus.label}
                                  </Badge>
                                </div>
                                {interaction.quoteTitle && (
                                  <p className="truncate text-xs text-muted-foreground">
                                    {interaction.quoteTitle}
                                  </p>
                                )}
                              </div>
                              <div className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                                <Eye className="h-3.5 w-3.5" />
                                {formatViewCount(interaction.viewCount)}
                              </div>
                            </div>

                            <div className="mt-3 grid gap-x-4 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-5">
                              <div className="min-w-0">
                                <p className="text-muted-foreground">Recipient</p>
                                <p className="truncate font-medium">{interaction.recipientEmail || '-'}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Views</p>
                                <p className="font-medium">{formatViewCount(interaction.viewCount)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">First viewed</p>
                                <p className="font-medium">{formatInteractionDate(interaction.firstViewedAt)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Last viewed</p>
                                <p className="font-medium">{formatInteractionDate(interaction.lastViewedAt)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Decision</p>
                                <p className="font-medium">
                                  {recipientStatus.timestamp
                                    ? formatInteractionDate(recipientStatus.timestamp)
                                    : recipientStatus.label}
                                </p>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </Skeleton>
              </TabsContent>
            )}
            {showPortalActivity && (
              <TabsContent value="portal" className="mt-0 animate-in fade-in-0 duration-200">
                {!portalActivityLoading && (portalActivityData?.totalOpens ?? 0n) === 0n ? (
                  <Empty className="border border-dashed py-12">
                    <EmptyMedia variant="icon">
                      <Link2 className="h-5 w-5" />
                    </EmptyMedia>
                    <EmptyDescription>No portal link activity yet</EmptyDescription>
                  </Empty>
                ) : (
                  <Skeleton
                    data={portalActivityLoading ? null : portalActivityData}
                    sample={SAMPLE_PORTAL_ACTIVITY}
                    label="Loading portal activity"
                  >
                    {(portalActivity) => (
                      <div className="space-y-3">
                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                          <PortalSummary label="Total opens" value={portalActivity.totalOpens} />
                          <PortalSummary label="Unique browsers" value={portalActivity.uniqueBrowsers} />
                          <PortalSummary label="Intended recipients" value={portalActivity.intendedRecipients} />
                          <PortalSummary label="Unidentified opens" value={portalActivity.unidentifiedOpenCount} />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Recipient names describe who a link was created for, not a verified identity of the opener.
                        </p>
                        <div className="space-y-2">
                          {portalActivity.activities.map((activity) => {
                            return (
                              <div key={activity.id} className="rounded-md border p-3">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-medium">
                                      {portalActivityHeadline(activity)}
                                    </p>
                                    <p className="mt-1 text-xs text-muted-foreground">
                                      {activity.openCount} {activity.openCount === 1 ? 'open' : 'opens'} · first {formatTimestamp(activity.firstOpenedAt, 'MMM d, yyyy h:mm a')}
                                    </p>
                                  </div>
                                  {activity.isBot && <Badge variant="secondary">Bot</Badge>}
                                </div>
                                <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
                                  <div>
                                    <p className="text-muted-foreground">Last opened</p>
                                    <p className="font-medium">{formatTimestamp(activity.lastOpenedAt, 'MMM d, yyyy h:mm a')}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Device</p>
                                    <p className="font-medium">{activity.deviceLabel || activity.deviceType || '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Location</p>
                                    <p className="font-medium">{activity.locationLabel || '-'}</p>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </Skeleton>
                )}
              </TabsContent>
            )}
            {showWorkflows && workflowSourceResourceId && (
              <TabsContent value="workflows" className="mt-0 animate-in fade-in-0 duration-200">
                <EntityWorkflowRuns
                  sourceResourceId={workflowSourceResourceId}
                  triggerEntityType={workflowTriggerEntityType ?? ''}
                />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </CardContent>

      {showNotesAndTasks && (
        <>
          <TaskSheet
            organizationId={organizationId}
            parentType={parentType}
            parentId={parentId}
            open={isTaskSheetOpen}
            onOpenChange={setIsTaskSheetOpen}
          />

          <NoteSheet
            organizationId={organizationId}
            parentType={parentType}
            parentId={parentId}
            note={editingNote}
            open={isNoteSheetOpen}
            onOpenChange={handleNoteSheetClose}
          />
        </>
      )}

      {showAttachments && (
        <AttachmentSheet
          organizationId={organizationId}
          parentType={parentType}
          parentId={parentId}
          open={isAttachmentSheetOpen}
          onOpenChange={setIsAttachmentSheetOpen}
        />
      )}

      {canCompose && (isEmailParent || isConversationsParent) && customerEmail && (
        <EmailComposeSheet
          mode="compose"
          organizationId={organizationId}
          parentType={parentType}
          parentId={parentId}
          inquiryId={composeInquiryId}
          contactId={composeContactId}
          toEmail={customerEmail}
          templateContext={getTemplateContext(parentType)}
          open={isEmailComposeOpen}
          onOpenChange={setIsEmailComposeOpen}
        />
      )}
    </Card>
  )
}

function ComposeNeedsInboxNote() {
  return <p className="mb-3 text-sm text-muted-foreground">{EMAIL_COMPOSE_NEEDS_INBOX_MESSAGE}</p>
}

function PortalSummary({ label, value }: { label: string; value?: bigint }) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{(value ?? 0n).toString()}</p>
    </div>
  )
}
